import { isApiError } from '@botpress/client'
import {
  Alert,
  Button,
  Footer,
  Input,
  showSuccessToast,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@bpinternal/ui-kit-next'
import { useState } from 'react'
import { Link, SelectMenuItems, Select } from '~/elements'
import { RoleOptions } from '~/features/users/components'
import { Role } from '~/features/users/types'
import { useCreateWorkspaceMember } from '~/hooks'
import { useSuspenseQuery } from '~/services'

type AddMemberDialogProps = {
  workspaceId: string
  initEmail?: string
  initRole?: Role
  close: () => void
}
export const AddMemberDialog = ({ workspaceId, initEmail, initRole, close }: AddMemberDialogProps) => {
  const { plan } = useSuspenseQuery('workspaces_/$workspaceId_', { workspaceId }).data
  const [role, setRole] = useState<Role>(initRole ? initRole : plan === 'community' ? 'administrator' : 'viewer')
  const [email, setEmail] = useState(initEmail || '')
  const { mutate: createWorkspaceMember, isPending } = useCreateWorkspaceMember()
  const [errorMessage, setErrorMessage] = useState<string | React.ReactNode | null>(null)

  const roleMenu: SelectMenuItems[] = RoleOptions.map((option) => ({
    type: 'item',
    value: option.role,
    disabled: option.role === role,
    content: (
      <div className="flex flex-col">
        <TooltipProvider>
          <Tooltip delayDuration={500}>
            <TooltipTrigger asChild>
              <span>{option.label}</span>
            </TooltipTrigger>
            <TooltipContent side="right" sideOffset={16} className="max-w-64">
              {option.description}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    ),
  }))

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-col gap-4">
        <div className="flex gap-4">
          <Input
            className="flex-grow"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value)
            }}
            placeholder="example@example.com"
          />
          {/* TODO: Add a tooltip option to the select component, this was done in a hurry */}
          {plan === 'community' ? (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button color="gray" disabled>
                    {role}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Role-based access control is only available in paid plans.</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            <Select
              value={role}
              onValueChange={(newRole: Role) => setRole(newRole)}
              color="gray"
              className="w-40"
              items={roleMenu}
            />
          )}
        </div>
      </div>

      <Footer
        //TODO: decide how to handle form validation in the modal
        disabled={!email}
        loading={isPending}
        onConfirm={() => {
          createWorkspaceMember({
            workspaceId,
            email,
            role,
            options: {
              onSuccess: async () => {
                showSuccessToast(`${email} has been 'invited' to this workspace as ${role}.`)
                close()
              },
              onError: async (err) => {
                if (isApiError(err) && err.type === 'Forbidden') {
                  setErrorMessage(
                    <>
                      <p>{err.message}</p>
                      <p className="mt-2">
                        To limit abuse, inviting new users to a workspace is only available in paid plans.{' '}
                        <Link to="/workspaces/$workspaceId/billing" params={{ workspaceId }} onClick={() => close()}>
                          Please upgrade here
                        </Link>{' '}
                        if you are ready to go.
                      </p>
                    </>
                  )
                } else {
                  setErrorMessage(err.message)
                }
              },
            },
          })
        }}
      />
      {errorMessage && <Alert variant="destructive">{errorMessage}</Alert>}
    </div>
  )
}
